import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { defineSection } from "@/ssr/define-section";
import { availableTopicsQuery } from "@/api-queries/available-topics";
import { HeroTopicsCarousel } from "./HeroTopicsCarousel";
import {
  HERO_FALLBACK_SRC,
  HERO_OBJECT_POSITION,
  HERO_SOURCE_HEIGHT,
  HERO_SOURCE_WIDTH,
  HERO_SOURCES,
} from "./hero-image";

// Reassurance line under the subheadline. Three claims, no more: the objections
// people actually raise before booking a first session.
const TRUST_KEYS = [
  "home.hero.trust.licensed",
  "home.hero.trust.confidential",
  "home.hero.trust.payment",
] as const;

function HeroView() {
  const ref = useScrollReveal<HTMLElement>();
  const { t } = useTranslation();

  return (
    <section
      ref={ref}
      className="animate-on-scroll relative flex min-h-[100dvh] flex-col justify-center overflow-hidden px-5 pb-28 pt-24 sm:px-6 sm:pb-32 lg:px-8"
      style={{ background: "var(--color-hero-ground)" }}
    >
      {/* Background photo. A real <img>, not a CSS background, so the browser
          finds it in the HTML (and the homepage preloads it, see
          hero-image.ts); it is the LCP candidate on most screens. */}
      <picture>
        {HERO_SOURCES.map((s) => (
          <source key={`${s.type} ${s.media ?? ""}`} type={s.type} media={s.media} srcSet={s.srcSet} sizes={s.sizes} />
        ))}
        <img
          src={HERO_FALLBACK_SRC}
          alt=""
          aria-hidden
          width={HERO_SOURCE_WIDTH}
          height={HERO_SOURCE_HEIGHT}
          fetchPriority="high"
          className="absolute inset-0 z-0 h-full w-full object-cover"
          style={{ objectPosition: HERO_OBJECT_POSITION }}
        />
      </picture>
      {/* Petrol tint over the photo, in two passes that together give it a
          shape rather than a flat wash.

          The ellipse opens a clearing over the subject and closes down toward
          every edge, so the photo is brightest exactly where someone is looking
          and the frame darkens on its own. The linear sweep then leans the
          whole thing left, where the headline, the CTA and the card row all
          need an opaque ground to read against. */}
      <div
        aria-hidden
        className="absolute inset-0 z-0"
        style={{
          background: [
            "radial-gradient(115% 95% at 74% 38%, transparent 0%, color-mix(in oklab, var(--color-hero-ground) 26%, transparent) 52%, color-mix(in oklab, var(--color-hero-ground-deep) 66%, transparent) 100%)",
            "linear-gradient(103deg, var(--color-hero-ground) 0%, var(--color-hero-ground) 20%, color-mix(in oklab, var(--color-hero-ground) 90%, transparent) 44%, color-mix(in oklab, var(--color-hero-ground) 62%, transparent) 72%, color-mix(in oklab, var(--color-hero-ground) 42%, transparent) 100%)",
          ].join(", "),
        }}
      />
      {/* Third pass down the top edge, so the transparent header sits on a
          darker band than the middle of the photo and its links stay legible. */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 z-0 h-40"
        style={{
          background:
            "linear-gradient(to bottom, color-mix(in oklab, var(--color-hero-ground-deep) 78%, transparent) 0%, transparent 100%)",
        }}
      />

      {/* Same container as the header, so the headline's left edge lands on the
          logo's. */}
      <div className="relative z-10 mx-auto w-full max-w-7xl">
        {/* leading has to clear the carons on š/č/ž: anything tighter than
            ~1.05 collides them with descenders from the line above. */}
        <h1
          className="font-heading animate-hero-rise max-w-[22ch] font-black leading-[1.06] tracking-[-0.035em]"
          style={{
            fontSize: "clamp(2.25rem, 5.2vw, 3.75rem)",
            color: "var(--color-paper)",
            animationDelay: "60ms",
          }}
        >
          {t("home.hero.choose.headline")}
        </h1>
        <p
          className="animate-hero-rise mt-5 max-w-[44ch] text-base leading-relaxed sm:text-lg"
          style={{
            color: "color-mix(in oklab, var(--color-paper) 78%, transparent)",
            animationDelay: "180ms",
          }}
        >
          {t("home.hero.choose.subheadline")}
        </p>

        {/* The dots only appear once the row fits on one line. Below that the
            list wraps, and a separator stranded at the start of a new line
            reads as a typo. */}
        <ul
          className="animate-hero-rise mt-5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm font-medium sm:gap-x-3"
          style={{ color: "var(--color-hero-gold)", animationDelay: "240ms" }}
        >
          {TRUST_KEYS.map((key, i) => (
            <li key={key} className="flex items-center gap-3">
              {i > 0 && (
                <span aria-hidden className="hidden opacity-55 sm:inline">
                  ·
                </span>
              )}
              {t(key)}
            </li>
          ))}
        </ul>

        <div className="animate-hero-rise mt-7" style={{ animationDelay: "320ms" }}>
          <Link
            to="/pronadji-terapeuta"
            className="hero-cta inline-flex min-h-14 items-center justify-center rounded-xl px-8 py-3 text-center text-base font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            {t("home.hero.ctaFind")}
          </Link>
        </div>

        {/* Themes active therapists offer — each links to the filtered list */}
        <HeroTopicsCarousel />
      </div>

      {/* Wavy bottom border bridging into the cream section below */}
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-[-1px] z-20 h-[70px] w-full sm:h-[110px]"
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0,72 C240,16 480,16 720,52 C960,88 1200,104 1440,56 L1440,120 L0,120 Z"
          fill="oklch(98.5% 0.008 84)"
        />
      </svg>
    </section>
  );
}

export const Hero = defineSection({ Component: HeroView, queries: [availableTopicsQuery] });
